import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Scales, EnvelopeSimple, CalendarBlank, Gavel, ArrowRight, CheckCircle, CircleNotch,
  UsersThree, Buildings, Receipt,
} from '@phosphor-icons/react';
import { fetchMyProfile, completeFirstRun, type ProfileData } from '@/lib/meApi';
import { useAuthStore } from '@/features/auth/store';
import { routeForMember } from '@/features/auth/hooks';

/**
 * First-run experience (/welcome) — the post-activation Welcome → First Value → First Success arc.
 *
 * Role-aware. A **chambers admin** lands on the chambers set-up arc: their plan (set by the Super
 * Admin at tenant creation) → "set up your team" (invite barristers & clerks) → tune reference data.
 * A **barrister** lands on the solo-practice arc (inbox / diary / new matter). Both speak in
 * OUTCOMES, not software, and route into the real pages — never reimplementing them. Completing or
 * skipping stamps User.firstRunCompletedAt so it shows once.
 */
type Phase = 'loading' | 'choose' | 'success';

export default function FirstRunPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [phase, setPhase] = useState<Phase>('loading');
  const [busy, setBusy] = useState(false);
  const [profile, setProfile] = useState<ProfileData | null>(null);

  const isAdmin = user?.memberType === 'admin';
  const dash = user ? routeForMember(user.memberType) : '/login';

  // Guard: not signed in → login; already completed → straight to the dashboard (show once).
  useEffect(() => {
    let active = true;
    (async () => {
      if (!user) { navigate('/login', { replace: true }); return; }
      try {
        const p = await fetchMyProfile();
        if (!active) return;
        setProfile(p);
        if (p.firstRunCompleted) { navigate(dash, { replace: true }); return; }
      } catch { /* if profile can't load, still show the welcome rather than dead-end */ }
      if (active) setPhase('choose');
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function startWith(to: string) {
    setBusy(true);
    try { await completeFirstRun(); } catch { /* non-fatal — don't block the barrister */ }
    navigate(to, { replace: true });
  }

  async function finishLater() {
    setBusy(true);
    try { await completeFirstRun(); } catch { /* non-fatal */ }
    setBusy(false);
    setPhase('success');
  }

  if (phase === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f8f7f4]">
        <CircleNotch className="h-6 w-6 animate-spin text-[#1a2f4e]" />
      </div>
    );
  }

  if (phase === 'success') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f8f7f4] p-6">
        <div className="w-full max-w-md text-center">
          <div className="mb-5 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
              <CheckCircle className="h-8 w-8 text-emerald-600 dark:text-emerald-400" weight="fill" />
            </div>
          </div>
          <h1 className="mb-2 text-xl font-semibold text-[#1a1915]">
            {isAdmin ? 'Your chambers is ready' : 'Your practice is ready'}
          </h1>
          <p className="mb-8 text-sm text-[#6b6961]">
            {isAdmin
              ? 'Your workspace is live. Invite your barristers and clerks whenever you’re ready — each gets their own secure login and only the access you grant.'
              : 'Your diary is set up and your workspace is live. Whenever you’re ready, bring your inbox in and add your first instruction — everything will appear here automatically.'}
          </p>
          <button
            type="button"
            onClick={() => navigate(dash, { replace: true })}
            className="inline-flex items-center justify-center gap-2 rounded bg-[#1a2f4e] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#243d61]"
          >
            {isAdmin ? 'Go to my chambers' : 'Go to my practice'} <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  const barristerCards = [
    {
      icon: <EnvelopeSimple className="h-5 w-5" />,
      title: 'Bring today’s instructions into Jurivoxa',
      body: 'Connect your inbox so new instructions land against the right matter automatically.',
      to: '/barrister/inbox',
    },
    {
      icon: <CalendarBlank className="h-5 w-5" />,
      title: 'See your upcoming hearings automatically',
      body: 'Connect your calendar and your diary stays in sync — no double-entry.',
      to: '/barrister/diary',
    },
    {
      icon: <Gavel className="h-5 w-5" />,
      title: 'Start your first instruction',
      body: 'Add a matter and watch it appear in your diary, timeline and fees in one place.',
      to: '/barrister/matters/new',
    },
  ];

  // Chambers-admin arc — your team is the first real action; reference data second.
  const adminCards = [
    {
      icon: <UsersThree className="h-5 w-5" />,
      title: 'Set up your team',
      body: 'Invite your barristers and clerks — each gets their own secure login and only the access you grant.',
      to: '/admin/settings/users',
    },
    {
      icon: <Buildings className="h-5 w-5" />,
      title: 'Tune your practice areas & courts',
      body: 'Adjust the lists your matters draw from — we’ve pre-loaded the national courts to get you started.',
      to: '/admin/practice-areas',
    },
  ];

  const cards = isAdmin ? adminCards : barristerCards;

  // "Your plan" panel (chambers admin only) — the payments slot. Shows the plan the Super Admin set
  // at tenant creation (Chambers.plan/trialEndsAt). No fake checkout: real billing lands here later.
  const plan = profile?.chambers.plan ?? '';
  const planLabel = plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : 'Trial';
  const trialEndsAt = profile?.chambers.trialEndsAt ?? null;
  let trialLine = '';
  if (trialEndsAt) {
    const days = Math.max(0, Math.ceil((new Date(trialEndsAt).getTime() - Date.now()) / 86_400_000));
    trialLine = ` — ${days} day${days === 1 ? '' : 's'} left`;
  }

  return (
    <div className="min-h-screen bg-[#f8f7f4]">
      <header className="border-b border-[#e5e3de] bg-white px-8 py-4">
        <div className="mx-auto max-w-3xl">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded bg-[#1a2f4e]">
              <Scales className="h-4 w-4 text-white" weight="duotone" />
            </div>
            <span className="text-sm font-semibold tracking-tight text-[#1a1915]">Jurivoxa</span>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-6 py-14">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-[#1a1915]">
            Welcome{user?.firstName ? `, ${user.firstName}` : ''}.
          </h1>
          <p className="mt-1 text-sm text-[#6b6961]">
            {isAdmin
              ? 'Let’s get your chambers running. Start with your team — you can always come back to the rest.'
              : 'What would you like to do first? Pick one — you can always do the others later.'}
          </p>
        </div>

        {isAdmin && (
          <div className="mb-6 rounded-lg border border-[#e5e3de] bg-white p-5">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[#1a2f4e]/10 text-[#1a2f4e]">
                <Receipt className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium uppercase tracking-wide text-[#9c9990]">Your plan</div>
                <div className="text-sm font-semibold text-[#1a1915]">
                  {planLabel}{trialLine}
                </div>
                <div className="mt-1 text-xs text-[#6b6961]">
                  Billing and payment methods will appear here soon — there’s nothing to pay during your trial.
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {cards.map((c) => (
            <button
              key={c.to}
              type="button"
              disabled={busy}
              onClick={() => void startWith(c.to)}
              className="group flex w-full items-center gap-4 rounded-lg border border-[#e5e3de] bg-white p-5 text-left transition hover:border-[#1a2f4e]/40 hover:shadow-sm disabled:opacity-60"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[#1a2f4e]/10 text-[#1a2f4e]">
                {c.icon}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-[#1a1915]">{c.title}</div>
                <div className="text-xs text-[#6b6961]">{c.body}</div>
              </div>
              <ArrowRight className="h-4 w-4 text-[#9c9990] transition group-hover:translate-x-0.5 group-hover:text-[#1a2f4e]" />
            </button>
          ))}
        </div>

        <button
          type="button"
          disabled={busy}
          onClick={() => void finishLater()}
          className="mt-6 text-sm font-medium text-[#6b6961] underline-offset-2 hover:text-[#1a2f4e] hover:underline disabled:opacity-60"
        >
          {isAdmin ? 'I’ll set this up later — take me to my chambers' : 'I’ll set this up later — take me to my practice'}
        </button>
      </main>
    </div>
  );
}
